import React, { useEffect, useRef, useState } from "react";
import { Button, Card, CardBody, CardHeader, Col, Container, Table } from "reactstrap";
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { API_BASE_URL } from "../../../constants";
import axios from "axios";
import profileDefault from "../../../assets/images/icons/profile.png";
import { Toast } from "primereact/toast";
import { ChartNoAxesCombined } from "lucide-react";
import ReactApexChart from "react-apexcharts";

const StaffAttendanceReport = () => {
    const breadcrumbItems = [
        { title: "Staffs", link: "/" },
        { title: "Attendance", link: "#!" },
    ];

    const [attendanceData, setAttendanceData] = useState({});
    const [staffList, setStaffList] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(false);
    // const [loadingRender, setLoadingRender] = useState(false);
    const [analyticsData, setAnalyticsData] = useState(null);
    const toast = useRef(null);

    const isFutureDate = (date) => {
        const today = new Date().toISOString().split('T')[0];
        return date > today;
    };

    const fetchStaffAttendance = React.useCallback(async () => {
        if (isFutureDate(selectedDate)) {
            toast.current.show({
                severity: "warn",
                summary: "Warning",
                detail: "Cannot fetch attendance for future dates.",
                life: 3000,
            });
            return;
        }
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/staff-attendance/get?date=${selectedDate}`, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("token"),
                },
            });

            if (response.data?.status === "success") {
                setStaffList(response.data.data);

                // Initialize attendanceData with the fetched attendance records
                const initialAttendanceData = {};
                response.data.data.forEach((staff) => {
                    if (staff.attendances?.length > 0) {
                        const attendance = staff.attendances[0];
                        initialAttendanceData[staff.id] = {
                            status: attendance.status || "Not Marked",
                            check_in: attendance.check_in || "",
                            check_out: attendance.check_out || "",
                            remarks: attendance.remarks || "",
                        };
                    } else {
                        initialAttendanceData[staff.id] = {
                            status: "Not Marked",
                            check_in: "",
                            check_out: "",
                            remarks: "",
                        };
                    }
                });
                setAttendanceData(initialAttendanceData);
            } else {
                setStaffList([]);
                setAttendanceData({});
            }
        } catch (error) {
            console.error("Error fetching staff attendance:", error.response?.data || error);
            toast.current.show({ severity: "error", summary: "Error", detail: "Failed to fetch staff attendance data", life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [selectedDate]);

    const fetchAttendanceAnalytics = React.useCallback(async () => {
        if (isFutureDate(selectedDate)) {
            toast.current.show({
                severity: "warn",
                summary: "Warning",
                detail: "Cannot fetch analytics for future dates.",
                life: 3000,
            });
            return;
        }
        try {
            const response = await axios.get(`${API_BASE_URL}/staff-attendance/analysis?date=${selectedDate}`, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("token"),
                },
            });

            if (response.data?.status === "success") {
                setAnalyticsData(response.data.data);
            } else {
                setAnalyticsData(null);
            }
        } catch (error) {
            console.error("Error fetching attendance analytics:", error.response?.data || error);
            toast.current.show({ severity: "error", summary: "Error", detail: "Failed to fetch attendance analytics", life: 3000 });
        }
    }, [selectedDate]);

    const scrollToAnalytics = () => {
        const analyticsSection = document.getElementById("attendance-analytics");
        if (analyticsSection) {
            analyticsSection.scrollIntoView({ behavior: "smooth" });
        }
    };

    const handleDateChange = (date) => {
        const localDate = new Date(date[0].getTime() - date[0].getTimezoneOffset() * 60000);
        const formattedDate = localDate.toISOString().split('T')[0];
        setSelectedDate(formattedDate);

        if (isFutureDate(formattedDate)) {
            toast.current.show({
                severity: "warn",
                summary: "Warning",
                detail: "Cannot mark attendance for future dates.",
                life: 3000,
            });
        }
    };

    useEffect(() => {
        fetchStaffAttendance();
        fetchAttendanceAnalytics();
    }, [fetchStaffAttendance, fetchAttendanceAnalytics]);

    const getAttendanceChartOptions = () => {
        if (!analyticsData) return null;

        return {
            series: [
                analyticsData.present,
                analyticsData.absent,
                analyticsData.half_day,
                analyticsData.leave
            ],
            chart: {
                type: 'donut',
                height: 350
            },
            labels: ['Present', 'Absent', 'Half-day', 'Leave'],
            colors: ['#34c38f', '#f46a6a', '#50a5f1', '#f1b44c'],
            legend: {
                position: 'right',
            },
            responsive: [{
                breakpoint: 480,
                options: {
                    chart: {
                        width: 300
                    },
                    legend: {
                        position: 'bottom'
                    }
                }
            }],
            tooltip: {
                y: {
                    formatter: function (value) {
                        return value + " staff";
                    }
                }
            }
        };
    };

    const formatTimeDisplay = (time) => {
        if (!time) return "-";
        // Remove seconds if present
        return time.split(":").slice(0, 2).join(":");
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Toast ref={toast} />
                    <Breadcrumbs title="Staff Attendance" breadcrumbItems={breadcrumbItems} />
                    <Card>
                        <CardHeader className="align-items-center d-flex justify-content-between flex-wrap">
                            <h4 className="card-title mb-0 flex-grow-1">Attendance</h4>

                            <div className="d-none d-md-flex gap-2">
                                <Button
                                    color="success"
                                    className="d-flex align-items-center"
                                    onClick={scrollToAnalytics}
                                    style={{
                                        height: "35px",
                                        fontSize: "14px",
                                        padding: "0 12px"
                                    }}
                                >
                                    <ChartNoAxesCombined className="mdi mdi-chart-line me-1" style={{ fontSize: "16px" }} />
                                    <span>Analytics</span>
                                </Button>

                                <div className="d-flex gap-2">
                                    <Flatpickr
                                        className="form-control d-block"
                                        placeholder="Select Date"
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        options={{
                                            altInput: true,
                                            altFormat: "F j, Y",
                                            dateFormat: "Y-m-d",
                                            maxDate: "today",
                                        }}
                                        style={{ height: "35px", fontSize: "14px", padding: "2px 8px" }}
                                    />
                                    <Button
                                        color="primary"
                                        onClick={fetchStaffAttendance}
                                        style={{ height: "35px", fontSize: "14px", padding: "2px 10px" }}
                                    >
                                        Search
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>

                        <div className="d-block d-md-none mt-3">
                            <div className="d-flex flex-column gap-2">
                                <Button
                                    color="success"
                                    className="d-flex align-items-center justify-content-center w-100"
                                    onClick={scrollToAnalytics}
                                    style={{
                                        height: "35px",
                                        fontSize: "14px",
                                        padding: "0 12px"
                                    }}
                                >
                                    <ChartNoAxesCombined className="mdi mdi-chart-line me-1" style={{ fontSize: "16px" }} />
                                    <span>Analytics</span>
                                </Button>

                                <div className="d-flex flex-column gap-2">
                                    <Flatpickr
                                        className="form-control d-block w-100"
                                        placeholder="Select Date"
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        options={{
                                            altInput: true,
                                            altFormat: "F j, Y",
                                            dateFormat: "Y-m-d",
                                            maxDate: "today",
                                        }}
                                        style={{ height: "35px", fontSize: "14px", padding: "2px 8px" }}
                                    />
                                    <Button
                                        color="primary"
                                        onClick={fetchStaffAttendance}
                                        className="w-100"
                                        style={{ height: "35px", fontSize: "14px", padding: "2px 10px" }}
                                    >
                                        Search
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <CardBody>
                            <div className="table-responsive">
                                <Table bordered className="mb-0">
                                    <thead>
                                        <tr className="table-info">
                                            <th>SI.No</th>
                                            <th>Photo</th>
                                            <th>Name</th>
                                            <th>Details</th>
                                            <th>Check-In</th>
                                            <th>Check-Out</th>
                                            <th>Remarks</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="8" className="text-center">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : staffList.length > 0 ? (
                                            staffList.map((staff, index) => {
                                                const attendance = attendanceData[staff.id] || {};
                                                const attendanceStatus = attendance.status || "Not Marked";
                                                const checkIn = formatTimeDisplay(attendance.check_in);
                                                const checkOut = formatTimeDisplay(attendance.check_out);
                                                const remarks = attendance.remarks || "";

                                                return (
                                                    <tr key={staff.id}>
                                                        <th scope="row">{index + 1}</th>
                                                        <td>
                                                            <img src={staff.profile_pic || profileDefault} alt="Staff" className="rounded-circle" width="50" height="50" />
                                                        </td>
                                                        <td>{staff.name}</td>
                                                        <td>
                                                            <ul className="list-unstyled mb-0">
                                                                <li>Employee ID: {staff.employee_id}</li>
                                                                <li>Department: {staff.department}</li>
                                                                <li>Designation: {staff.designation}</li>
                                                            </ul>
                                                        </td>
                                                        <td>
                                                            {checkIn}
                                                        </td>
                                                        <td>
                                                            {checkOut}
                                                        </td>
                                                        <td>
                                                            {remarks}
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${attendanceStatus === "present" ? "bg-success-subtle text-success" :
                                                                attendanceStatus === "absent" ? "bg-danger-subtle text-danger" :
                                                                    attendanceStatus === "half-day" ? "bg-info-subtle text-info" :
                                                                        attendanceStatus === "leave" ? "bg-secondary-subtle text-secondary" : "bg-light text-dark"} text-uppercase`}>
                                                                {attendanceStatus}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center">No staff found for the selected date.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>

                    <Col lg={6}>
                        <Card id="attendance-analytics" className="mt-4">
                            <CardHeader>
                                <h4 className="card-title mb-0">Attendance Analytics</h4>
                            </CardHeader>
                            <CardBody>
                                {analyticsData ? (
                                    <div>
                                        <div className="mb-4">
                                            <h5>Attendance Distribution</h5>
                                            <div id="attendance-donut-chart">
                                                <ReactApexChart
                                                    options={getAttendanceChartOptions()}
                                                    series={getAttendanceChartOptions().series}
                                                    type="donut"
                                                    height={350}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <h5>Summary</h5>
                                            <ul>
                                                <li>Total Staff: {analyticsData.total_staff}</li>
                                                <li>Present: {analyticsData.present}</li>
                                                <li>Absent: {analyticsData.absent}</li>
                                                <li>Half-day: {analyticsData.half_day}</li>
                                                <li>Leave: {analyticsData.leave}</li>
                                            </ul>
                                        </div>
                                    </div>
                                ) : (
                                    <p>No analytics data available for the selected date.</p>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default StaffAttendanceReport;